// AdminBillingPage.jsx
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { MainContainer } from '../MainContainer'
import {
    getAllTransactionsAdmin,
    searchTransactionsAdmin,
    savePromoCodeAdmin,
    getAllPromosAdmin,
    searchPromosAdmin,
    togglePromoCodeAdmin,
    archivePromoCodeAdmin
} from '../../actions/adminBilling.action'
import { searchUser } from '../../actions/user.actions'
import Loader from '../../components/Loader'
import Modal from '../../components/UI/Modal'

const AdminBillingPage = () => {
    const dispatch = useDispatch()

    const {
        transactions = [],
        promos = [],
        analytics = {},
        loading = false,
        totalAll = 0,
        totalPages = 0
    } = useSelector(state => state.adminBilling || {})

    const usersList = useSelector(state => state.user?.users || [])

    const [activeTab, setActiveTab] = useState('promos')
    const [statusFilter, setStatusFilter] = useState('all')
    const [pageNumber, setPageNumber] = useState(0)
    const [pageSize, setPageSize] = useState(20)
    const [expandedPromoId, setExpandedPromoId] = useState(null)
    const [showPromoModal, setShowPromoModal] = useState(false)

    const [promoForm, setPromoForm] = useState({
        _id: null,
        code: '',
        type: 'FIXED',
        amount: '',
        subscriptionDays: '',
        maxUses: 1,
        expireValue: '',
        expireUnit: 'days',
        targetUserId: '',
        source: '',
        minTopUpAmount: '',
        scope: 'balance'
    })

    const [userSearchText, setUserSearchText] = useState('')
    const [selectedUser, setSelectedUser] = useState(null)

    useEffect(() => {
        setPageNumber(0)
        setExpandedPromoId(null)
    }, [activeTab, statusFilter])

    const additionalData = useMemo(() => ({
        activeTab,
        statusFilter,
        pageSize
    }), [activeTab, statusFilter, pageSize])

    const getAction = activeTab === 'transactions'
        ? getAllTransactionsAdmin
        : getAllPromosAdmin

    const searchAction = activeTab === 'transactions'
        ? searchTransactionsAdmin
        : searchPromosAdmin

    const typeSort = activeTab === 'transactions'
        ? 'transaction'
        : 'promo'

    const handleOpenCreate = () => {
        setPromoForm({
            _id: null,
            code: '',
            type: 'FIXED',
            amount: '',
            subscriptionDays: '',
            maxUses: 1,
            expireValue: '',
            expireUnit: 'days',
            targetUserId: '',
            source: '',
            minTopUpAmount: '',
            scope: 'balance'
        })
        setSelectedUser(null)
        setUserSearchText('')
        setShowPromoModal(true)
    }

    const handleOpenEdit = (promo) => {
        setPromoForm({
            _id: promo._id,
            code: promo.code,
            type: promo.type || 'FIXED',
            amount: promo.amount || '',
            subscriptionDays: promo.subscriptionDays || '',
            maxUses: promo.maxUses,
            expireValue: '',
            expireUnit: 'days',
            targetUserId: promo.targetUser?._id || '',
            source: promo.source || '',
            minTopUpAmount: promo.minTopUpAmount || '',
            scope: promo.scope || 'balance'
        })
        setSelectedUser(promo.targetUser || null)
        setUserSearchText('')
        setShowPromoModal(true)
    }

    const handlePromoTypeChange = (type) => {
        let scope = 'balance'

        if (type === 'PERCENTAGE') scope = 'topup'
        if (type === 'SUBSCRIPTION') scope = 'subscription'
        if (type === 'DISCOUNT') scope = 'scene'

        setPromoForm({
            ...promoForm,
            type,
            scope
        })
    }

    const handleSavePromo = async () => {
        if (!promoForm.code) return

        const payload = {
            ...promoForm,
            targetUserId: selectedUser ? selectedUser._id : null
        }

        const success = await dispatch(savePromoCodeAdmin(payload, additionalData))

        if (success) {
            setShowPromoModal(false)
        }
    }

    const handleTogglePromo = (id) => {
        dispatch(togglePromoCodeAdmin(id, additionalData))
    }

    const handleArchivePromo = (id) => {
        if (window.confirm('Удалить в архив? Код перестанет работать и будет скрыт.')) {
            dispatch(archivePromoCodeAdmin(id, additionalData))
        }
    }

    const handleUserSearch = (e) => {
        const text = e.target.value
        setUserSearchText(text)

        if (text.length > 2) {
            dispatch(searchUser(text))
        }
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return ''

        const d = new Date(dateStr)

        return d.toLocaleDateString('ru-RU') + ' ' + d.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const renderTransactions = () => {
        if (transactions.length === 0) {
            return <div className="admin-billing__empty">Транзакций пока нет.</div>
        }

        return (
            <div className="admin-billing__content">
                <table className="admin-billing__table">
                    <thead>
                        <tr>
                            <th>Дата</th>
                            <th>Субъект</th>
                            <th>Сумма</th>
                            <th>Тип</th>
                            <th>Баланс</th>
                            <th>Промокод</th>
                            <th>Описание</th>
                        </tr>
                    </thead>
                    <tbody>
                        {transactions.map(tx => (
                            <tr key={tx._id}>
                                <td>{formatDate(tx.createdAt)}</td>
                                <td>
                                    {tx.targetModel} {tx.targetId?.firstName || tx.targetId?.name || tx.targetId?.email || tx.targetId}
                                </td>
                                <td className={tx.amount > 0 ? 'admin-billing__text--positive' : 'admin-billing__text--neutral'}>
                                    {tx.amount > 0 ? '+' : ''}{tx.amount} ₽
                                </td>
                                <td>{tx.type}</td>
                                <td>
                                    {tx.balanceBefore !== null && tx.balanceBefore !== undefined ? tx.balanceBefore : '—'}
                                    {' → '}
                                    {tx.balanceAfter !== null && tx.balanceAfter !== undefined ? tx.balanceAfter : '—'}
                                </td>
                                <td>{tx.promoCode?.code || '—'}</td>
                                <td>{tx.description}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    const renderPromos = () => {
        return (
            <div className="admin-billing__content">
                <div className="admin-billing__status-filters">
                    <span
                        className={statusFilter === 'all' ? 'active' : ''}
                        onClick={() => setStatusFilter('all')}
                    >
                        Все кроме архива
                    </span>
                    <span
                        className={statusFilter === 'active' ? 'active' : ''}
                        onClick={() => setStatusFilter('active')}
                    >
                        Активные
                    </span>
                    <span
                        className={statusFilter === 'inactive' ? 'active' : ''}
                        onClick={() => setStatusFilter('inactive')}
                    >
                        Отключенные
                    </span>
                    <span
                        className={statusFilter === 'archived' ? 'active' : ''}
                        onClick={() => setStatusFilter('archived')}
                    >
                        Архив
                    </span>
                </div>

                <div className="admin-billing__analytics-card">
                    <div className="admin-billing__analytics-card-info">
                        <h4>Аналитика по кодам</h4>
                        <p>
                            Фактически начислено бонусами: <b>{analytics.totalVirtualMoneyGiven || 0} ₽</b>
                        </p>
                    </div>
                </div>

                {promos.length === 0 ? (
                    <div className="admin-billing__empty">Ничего не найдено.</div>
                ) : (
                    <table className="admin-billing__table">
                        <thead>
                            <tr>
                                <th>Код</th>
                                <th>Тип / Бонус</th>
                                <th>Область</th>
                                <th>Статус</th>
                                <th>Метка UTM</th>
                                <th>Использований</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {promos.map(p => {
                                const usedBy = Array.isArray(p.usedBy) ? p.usedBy : []
                                const isExpired = p.expiresAt && new Date(p.expiresAt) < new Date()
                                const isExhausted = p.maxUses > 0 && usedBy.length >= p.maxUses
                                const statusText = p.isArchived
                                    ? 'Архив'
                                    : !p.isActive
                                        ? 'Отключен'
                                        : isExpired
                                            ? 'Истек'
                                            : isExhausted
                                                ? 'Исчерпан'
                                                : 'Активен'

                                const isExpanded = expandedPromoId === p._id
                                const typeLabel = p.type === 'PERCENTAGE'
                                    ? `${p.amount}%`
                                    : p.type === 'SUBSCRIPTION'
                                        ? `${p.subscriptionDays} дней`
                                        : `${p.amount} ₽`

                                let statusClass = 'admin-billing__text--neutral'

                                if (p.isActive && !isExpired && !isExhausted && !p.isArchived) {
                                    statusClass = 'admin-billing__text--positive'
                                } else if (!p.isActive || isExpired || isExhausted || p.isArchived) {
                                    statusClass = 'admin-billing__text--negative'
                                }

                                return (
                                    <React.Fragment key={p._id}>
                                        <tr className={isExpanded ? 'admin-billing__table-row--expanded' : ''}>
                                            <td style={{ fontWeight: 'bold', fontSize: '16px' }}>{p.code}</td>
                                            <td style={{ fontWeight: 'bold' }}>{typeLabel}</td>
                                            <td>{p.scope || '—'}</td>
                                            <td className={statusClass}>{statusText}</td>
                                            <td>{p.source || '—'}</td>
                                            <td>{usedBy.length} / {p.maxUses === 0 ? '∞' : p.maxUses}</td>
                                            <td>
                                                <div className="admin-billing__actions">
                                                    <button
                                                        className="admin-billing__actions-btn--default"
                                                        onClick={() => setExpandedPromoId(isExpanded ? null : p._id)}
                                                    >
                                                        {isExpanded ? 'Свернуть' : 'Детали'}
                                                    </button>
                                                    <button
                                                        className="admin-billing__actions-btn--edit"
                                                        onClick={() => handleOpenEdit(p)}
                                                    >
                                                        Изменить
                                                    </button>
                                                    <button
                                                        className={`admin-billing__actions-btn--toggle ${p.isActive ? 'active' : 'inactive'}`}
                                                        onClick={() => handleTogglePromo(p._id)}
                                                    >
                                                        {p.isActive ? 'Выкл' : 'Вкл'}
                                                    </button>
                                                    {!p.isArchived && (
                                                        <button
                                                            className="admin-billing__actions-btn--archive"
                                                            onClick={() => handleArchivePromo(p._id)}
                                                        >
                                                            Архив
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>

                                        {isExpanded && (
                                            <tr className="admin-billing__table-details">
                                                <td colSpan="7">
                                                    <div className="admin-billing__table-details-content">
                                                        <div>
                                                            <h5>Параметры кода:</h5>
                                                            <p>Действует до: {p.expiresAt ? formatDate(p.expiresAt) : 'Бессрочно'}</p>
                                                            <p>Минимальное пополнение: {p.minTopUpAmount || 0} ₽</p>
                                                            <p>
                                                                Целевой клиент: {p.targetUser
                                                                    ? `${p.targetUser.firstName || ''} ${p.targetUser.lastName || ''} (${p.targetUser.email || ''})`
                                                                    : 'Любой'}
                                                            </p>
                                                            <p>
                                                                Создал: {p.createdBy
                                                                    ? `${p.createdBy.firstName || ''} ${p.createdBy.lastName || ''} (${p.createdBy.email || ''})`
                                                                    : '—'}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <h5>Кто использовал:</h5>
                                                            {usedBy.length === 0 ? (
                                                                <p>Никто не использовал</p>
                                                            ) : (
                                                                <ul>
                                                                    {usedBy.map((usage, idx) => (
                                                                        <li key={idx}>
                                                                            {usage.user?.firstName} {usage.user?.lastName} ({usage.user?.email}) — {formatDate(usage.usedAt)} — <b>Бонус: {usage.bonusAmount}</b>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        )
    }

    return (
        <div className="admin-billing">
            <MainContainer
                title="Биллинг и Промокоды"
                buttonText="Создать Промокод"
                handleShow={handleOpenCreate}
                fluid
                tabs
                itemsType="adminBilling"
                setValueTabsSort={setActiveTab}
                typeSort={typeSort}
                get={getAction}
                search={searchAction}
                additionalData={additionalData}
                setPageNumber={setPageNumber}
                setPageSize={setPageSize}
                pageNumber={pageNumber}
                pageSize={pageSize}
                totalAll={totalAll}
                totalPages={totalPages}
                noneSelectUpdatedPictures
            >
                {loading ? <Loader /> : activeTab === 'transactions' ? renderTransactions() : renderPromos()}
            </MainContainer>

            <Modal
                show={showPromoModal}
                handleClose={() => setShowPromoModal(false)}
                modalTitle={promoForm._id ? 'Редактирование кода' : 'Генерация нового кода'}
                size="md"
            >
                <div className="admin-billing__modal">
                    <div className="admin-billing__modal-group">
                        <label>Код например SALE1000</label>
                        <input
                            type="text"
                            disabled={!!promoForm._id}
                            value={promoForm.code}
                            onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                            style={{ background: promoForm._id ? '#eee' : '#fff' }}
                        />
                    </div>

                    <div className="admin-billing__modal-group">
                        <label>Тип бонуса</label>
                        <select
                            value={promoForm.type}
                            onChange={(e) => handlePromoTypeChange(e.target.value)}
                        >
                            <option value="FIXED">Фиксированная сумма ₽</option>
                            <option value="PERCENTAGE">Процент при пополнении %</option>
                            <option value="SUBSCRIPTION">Дни подписки</option>
                            <option value="DISCOUNT">Скидка на сцену ₽</option>
                        </select>
                    </div>

                    <div className="admin-billing__modal-group">
                        <label>Область применения</label>
                        <select
                            value={promoForm.scope}
                            onChange={(e) => setPromoForm({ ...promoForm, scope: e.target.value })}
                        >
                            <option value="balance">Начисление на баланс</option>
                            <option value="topup">Бонус при пополнении</option>
                            <option value="subscription">Подписка</option>
                            <option value="scene">Скидка на сцену</option>
                            <option value="any">Любая область</option>
                        </select>
                    </div>

                    {promoForm.type === 'SUBSCRIPTION' ? (
                        <div className="admin-billing__modal-group">
                            <label>Количество дней</label>
                            <input
                                type="number"
                                value={promoForm.subscriptionDays}
                                onChange={(e) => setPromoForm({ ...promoForm, subscriptionDays: e.target.value })}
                            />
                        </div>
                    ) : (
                        <div className="admin-billing__modal-group">
                            <label>Значение {promoForm.type === 'PERCENTAGE' ? '%' : '₽'}</label>
                            <input
                                type="number"
                                value={promoForm.amount}
                                onChange={(e) => setPromoForm({ ...promoForm, amount: e.target.value })}
                            />
                        </div>
                    )}

                    {promoForm.type === 'PERCENTAGE' && (
                        <div className="admin-billing__modal-group">
                            <label>Минимальная сумма пополнения</label>
                            <input
                                type="number"
                                value={promoForm.minTopUpAmount}
                                onChange={(e) => setPromoForm({ ...promoForm, minTopUpAmount: e.target.value })}
                            />
                        </div>
                    )}

                    <div className="admin-billing__modal-row">
                        <div className="admin-billing__modal-group">
                            <label>Продлить на пусто = бессрочно</label>
                            <input
                                type="number"
                                value={promoForm.expireValue}
                                onChange={(e) => setPromoForm({ ...promoForm, expireValue: e.target.value })}
                            />
                        </div>
                        <div className="admin-billing__modal-group">
                            <label>Единица</label>
                            <select
                                value={promoForm.expireUnit}
                                onChange={(e) => setPromoForm({ ...promoForm, expireUnit: e.target.value })}
                            >
                                <option value="hours">Часов</option>
                                <option value="days">Дней</option>
                                <option value="months">Месяцев</option>
                            </select>
                        </div>
                    </div>

                    <div className="admin-billing__modal-group admin-billing__user-search">
                        <label>Привязать к конкретному пользователю опционально</label>
                        {selectedUser ? (
                            <div className="admin-billing__user-search-selected">
                                <span>{selectedUser.firstName} {selectedUser.lastName} ({selectedUser.email})</span>
                                <button onClick={() => setSelectedUser(null)}>✕ Отвязать</button>
                            </div>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    placeholder="Введите email или имя для поиска..."
                                    value={userSearchText}
                                    onChange={handleUserSearch}
                                />
                                {userSearchText.length > 2 && usersList.length > 0 && (
                                    <div className="admin-billing__user-search-dropdown">
                                        {usersList.map(u => (
                                            <div
                                                key={u._id}
                                                className="admin-billing__user-search-dropdown-item"
                                                onClick={() => {
                                                    setSelectedUser(u)
                                                    setUserSearchText('')
                                                }}
                                            >
                                                {u.firstName} {u.lastName} ({u.email})
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="admin-billing__modal-group">
                        <label>UTM Метка / Источник опционально</label>
                        <input
                            type="text"
                            placeholder="Yandex, Blogger_Ivan"
                            value={promoForm.source}
                            onChange={(e) => setPromoForm({ ...promoForm, source: e.target.value })}
                        />
                    </div>

                    <div className="admin-billing__modal-group">
                        <label>Лимит активаций 0 = безлимит</label>
                        <input
                            type="number"
                            value={promoForm.maxUses}
                            onChange={(e) => setPromoForm({ ...promoForm, maxUses: e.target.value })}
                        />
                    </div>

                    <button
                        className="admin-billing__modal-submit"
                        onClick={handleSavePromo}
                        disabled={loading || !promoForm.code || (!promoForm.amount && !promoForm.subscriptionDays)}
                    >
                        {loading ? 'Обработка...' : 'Сохранить код'}
                    </button>
                </div>
            </Modal>
        </div>
    )
}

export default AdminBillingPage